import { useCallback, useMemo, useState } from 'react';
import { ConfirmationPopup } from '../../../../components';
import {
  useFulfillmentAddHandler,
  useFulfillmentBulkGenerateTotesHandler,
} from '../../../../contexts/fulfillment-tab-actions';
import { ToteListWarehouseHook } from '../../../../hooks/tote';
import { AddTotePopup } from '../../../../page-components/waves';
import { ToastMessage, WaveService } from '../../../../utility';
import { ToteResetPopup } from '../../../../page-components/tote';
import { GenerateToteLabelPopup } from '@page-components/tote/GenerateLabelPopup';
import { BulkGenerateTotesModal } from '@page-components/tote/BulkGenerateTotesModal';
import { BulkGenerateToteLabelsModal } from '@page-components/tote/BulkGenerateToteLabelsModal';
import { TotesTable } from '../../../../page-components/settings/fulfillment/tote';
import { AlertFooterBar } from '../../../../elements/FooterAlert';

export function TotesTab({ warehouseId, search }) {
  const { data, totalData, loading, filter, filterChanged, pageChanged, refresh } =
    ToteListWarehouseHook(warehouseId, null, true, search);

  const [addRulePopup, setAddRulePopup] = useState(false);
  const [deleteTotePopup, setDeletePopup] = useState(null);
  const [processing, setProcessing] = useState(false);
  const [toteToReset, setToteToReset] = useState(null);
  const [labelToteNumber, setLabelToteNumber] = useState(null);
  const [toteLabelPopupOpen, setToteLabelPopupOpen] = useState(false);
  const [bulkGenerateTotesOpen, setBulkGenerateTotesOpen] = useState(false);
  const [bulkGenerateLabelsOpen, setBulkGenerateLabelsOpen] = useState(false);
  const [selectedById, setSelectedById] = useState({});

  const selectedItems = useMemo(() => Object.values(selectedById), [selectedById]);
  const selectedCount = selectedItems.length;
  const selectedToteNumbers = useMemo(
    () => selectedItems.map(item => item.toteNumber).filter(Boolean),
    [selectedItems],
  );

  const openToteLabelPopup = toteNumber => {
    setLabelToteNumber(toteNumber);
    setToteLabelPopupOpen(true);
  };

  const closeToteLabelPopup = () => {
    setLabelToteNumber(null);
    setToteLabelPopupOpen(false);
  };

  const onSelectRow = useCallback((item, selected) => {
    setSelectedById(prev => {
      const next = { ...prev };
      if (selected) {
        next[item.id] = item;
      } else {
        delete next[item.id];
      }
      return next;
    });
  }, []);

  const onSelectAllRows = useCallback((items, selected) => {
    setSelectedById(prev => {
      const next = { ...prev };
      items.forEach(item => {
        if (selected) {
          next[item.id] = item;
        } else {
          delete next[item.id];
        }
      });
      return next;
    });
  }, []);

  const clearBulkSelection = useCallback(() => {
    setSelectedById({});
  }, []);

  const handleBulkLabelsCompleted = useCallback(() => {
    setSelectedById({});
    refresh();
  }, [refresh]);

  const openBulkGenerateTotes = useCallback(() => {
    setBulkGenerateTotesOpen(true);
  }, []);

  const openPopup = useCallback(() => {
    setAddRulePopup(true);
  }, []);

  useFulfillmentAddHandler(openPopup);
  useFulfillmentBulkGenerateTotesHandler(openBulkGenerateTotes);

  const closePopup = () => {
    setAddRulePopup(false);
  };

  const editTote = tote => {
    setAddRulePopup(tote);
  };

  const onDeleteTote = tote => {
    setDeletePopup(tote);
  };

  const onCloseDelete = () => {
    setDeletePopup(null);
  };

  const deleteTote = async () => {
    try {
      setProcessing(true);
      await WaveService.deleteTote(deleteTotePopup.id);
      ToastMessage.success(`${deleteTotePopup.toteNumber} deleted successfully.`);
      setDeletePopup(null);
      refresh();
    } catch (error) {
      ToastMessage.apiError(error?.error || error);
    } finally {
      setProcessing(false);
    }
  };

  return (
    <div className={selectedCount > 0 ? 'pb-4 mb-4' : ''}>
      <TotesTable
        data={data || []}
        loading={loading}
        totalData={totalData}
        filter={filter}
        pageChanged={pageChanged}
        filterChanged={filterChanged}
        selectedById={selectedById}
        onSelectRow={onSelectRow}
        onSelectAllRows={onSelectAllRows}
        editTote={editTote}
        onDeleteTote={onDeleteTote}
        resetTote={tote => setToteToReset(tote)}
      />

      <AddTotePopup
        onClose={closePopup}
        open={addRulePopup}
        refresh={refresh}
        warehouseId={warehouseId}
        item={addRulePopup}
        openToteLabelPopup={openToteLabelPopup}
      />

      <ConfirmationPopup
        onConfirm={deleteTote}
        onClose={onCloseDelete}
        open={deleteTotePopup}
        loading={processing}
        title="Delete Tote"
        message={`Are you sure to delete ${deleteTotePopup?.toteNumber}`}
      />
      <ToteResetPopup
        open={!!toteToReset}
        tote={toteToReset}
        refresh={refresh}
        onClose={() => setToteToReset(null)}
      />
      <GenerateToteLabelPopup
        warehouseId={warehouseId}
        open={toteLabelPopupOpen}
        onClose={closeToteLabelPopup}
        toteNumber={labelToteNumber}
      />

      <BulkGenerateTotesModal
        open={bulkGenerateTotesOpen}
        onClose={() => setBulkGenerateTotesOpen(false)}
        warehouseId={warehouseId}
        onCompleted={refresh}
      />

      <BulkGenerateToteLabelsModal
        open={bulkGenerateLabelsOpen}
        onClose={() => setBulkGenerateLabelsOpen(false)}
        warehouseId={warehouseId}
        toteNumbers={selectedToteNumbers}
        onCompleted={handleBulkLabelsCompleted}
      />

      {selectedCount > 0 ? (
        <AlertFooterBar
          message={
            selectedCount === 1 ? '1 tote selected' : `${selectedCount} totes selected`
          }
          cancel={clearBulkSelection}
          cancelButtonText="Clear"
          save={() => setBulkGenerateLabelsOpen(true)}
          saveButtonText="Bulk Generate Labels"
        />
      ) : null}
    </div>
  );
}
